// "Recevoir" — opened from a share link `#/r/<secret>[/p]`.

import { Mesh } from '../core/mesh';
import { deriveAuthKey } from '../core/auth';
import { roomIdFor } from '../core/ids';
import { DropReceiver } from '../drop/receiver';
import { parseCtl } from '../drop/protocol';
import { cleanupOpfs } from '../drop/sinks';
import { h } from './dom';
import { deviceName, iceServers, wsUrl } from './common';
import { incomingPanel } from './incoming';
import type { View } from './view';

type Status = 'password' | 'connecting' | 'waiting' | 'connected' | 'auth-failed' | 'error';

export function receiveView(root: HTMLElement, secret: string, needsPassword: boolean): View {
  let status: Status = needsPassword ? 'password' : 'connecting';
  let error = '';
  let mesh: Mesh | null = null;
  let receiver: DropReceiver | null = null;
  let panel: ReturnType<typeof incomingPanel> | null = null;
  let destroyed = false;

  async function connect(password: string) {
    status = 'connecting';
    render();
    try {
      const roomId = await roomIdFor(secret);
      // Files of this link stay on disk (resume after a reload); older drops are removed.
      void cleanupOpfs(roomId.slice(0, 16));
      const m = new Mesh({ url: wsUrl(`/ws/room/${roomId}`), iceServers: await iceServers(), authKey: await deriveAuthKey(secret, password) });
      mesh = m;
      m.addEventListener('authed', (e) => {
        m.send((e as CustomEvent).detail.peer, { t: 'hello', name: deviceName() });
        if (status !== 'connected') ((status = 'connected'), render());
      });
      m.addEventListener('auth-failed', () => {
        if (receiver) return;
        status = 'auth-failed';
        m.close();
        mesh = null;
        render();
      });
      m.addEventListener('ctl', (e) => {
        const { peer, msg } = (e as CustomEvent).detail;
        const parsed = parseCtl(msg);
        if (parsed?.t !== 'manifest') return;
        // A new manifest replaces the current one only if that one is finished or its sender left.
        if (receiver && (receiver.complete === false && m.peers.has(receiver.peer))) return;
        panel?.destroy();
        receiver?.stop();
        receiver = new DropReceiver(m, peer, parsed.from, parsed.files, roomId.slice(0, 16));
        receiver.start();
        panel = incomingPanel(receiver);
        render();
      });
      await m.start();
      if (destroyed) return m.close();
      if (m.peers.size === 0 && status === 'connecting') status = 'waiting';
      m.addEventListener('peer', () => status === 'waiting' && ((status = 'connecting'), render()));
    } catch (e) {
      status = 'error';
      error = (e as Error).message;
    }
    render();
  }

  function passwordForm(message?: string): Node[] {
    const field = h('input', { type: 'password', class: 'field', autocomplete: 'current-password', placeholder: 'mot de passe', required: true });
    const form = h(
      'form',
      {
        class: 'stack',
        onsubmit: (e: Event) => {
          e.preventDefault();
          void connect(field.value);
        },
      },
      field,
      h('button', { class: 'btn primary', type: 'submit', text: 'Continuer' }),
    );
    queueMicrotask(() => field.focus());
    return [h('p', { class: message ? 'bad' : 'dim', text: message ?? 'Ce partage est protégé par un mot de passe.' }), form];
  }

  function render() {
    if (destroyed) return;
    const nodes: (Node | false)[] = [h('h1', { text: 'Recevoir des fichiers' })];
    if (status === 'password') nodes.push(...passwordForm());
    else if (status === 'auth-failed') {
      nodes.push(
        ...(needsPassword
          ? passwordForm('Mot de passe incorrect.')
          : [h('p', { class: 'bad', text: 'Impossible de vérifier l’expéditeur : le lien est peut-être incomplet, ou la connexion a été interceptée.' })]),
      );
    } else if (status === 'error') nodes.push(h('p', { class: 'bad', text: `Connexion impossible : ${error}` }));
    else if (!receiver) {
      nodes.push(
        h('p', {
          class: 'dim',
          text:
            status === 'waiting'
              ? 'En attente de l’expéditeur… Le lien ne fonctionne que tant que sa page est ouverte.'
              : status === 'connected'
                ? 'Connecté, réception de la liste des fichiers…'
                : 'Connexion à l’expéditeur…',
        }),
      );
    }
    if (panel) nodes.push(panel.el);
    root.replaceChildren(...nodes.filter((n): n is Node => !!n));
  }

  render();
  if (!needsPassword) void connect('');

  return {
    busy: () => !!receiver && !receiver.complete && !receiver.cancelled,
    destroy() {
      destroyed = true;
      panel?.destroy();
      receiver?.stop();
      mesh?.close();
    },
  };
}
